import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  use,
} from "react";
import {
  Play,
  Pause,
  Trash,
  RotateCcw,
  Clock,
  Flame,
  CheckCircle,
  Circle,
  Plus,
  X,
  ChefHat,
  Timer,
  AlertCircle,
  Users,
  ArrowLeft,
  ArrowRight,
  List,
  ChevronDown
} from "lucide-react";
import Seo from '../components/Seo';
import ServingsAdjuster from "../components/ServingsAdjuster";
import ExplanationWithTimes from "../components/utils/ExplanationWithTimes";
import { set } from "date-fns";
import isEqual from "lodash.isequal"; // ou équivalent
import api from "../api/api"; // Import de l'API configurée avec Axios
import { useNavigate } from "react-router-dom"; // Ajoute ceci si ce n'est pas déjà importé

// Fonctions utilitaires de formatage
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs
    .toString()
    .padStart(2, "0")}`;
};
const formatDurationHuman = (seconds) => {
  if (seconds == null || isNaN(seconds)) return '';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return sec ? `${m} min ${sec}s` : `${m} min`;
  }
  const h = Math.floor(s / 3600);
  const rem = s % 3600;
  const m = Math.floor(rem / 60);
  return m ? `${h} h ${m} min` : `${h} h`;
};
const RecipeCarousel = ({ activeRecipes, currentRecipeIndex, goToRecipe }) => {
const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  return (
    <div className="w-full py-2">
      {/* Mobile Dropdown Trigger (visible only on mobile) */}
      <div className="sm:hidden">
        <button
          onClick={toggleDropdown}
          className="w-full bg-white border border-gray-200 rounded-xl py-2 px-4 flex items-center justify-between text-sm font-semibold text-[#2C3E50] shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#87A96B]"
        >
          <span>Changer de recette dans la session</span>
          <span>{isDropdownOpen ? '▲' : '▼'}</span>
        </button>
      </div>

      {/* Mobile Dropdown Content (visible when open) / Desktop Horizontal Scroll */}
      <div
        className={`${
          isDropdownOpen ? 'block' : 'hidden'
        } sm:block sm:overflow-x-auto sm:py-2`}
      >
        <div
          className={`flex flex-col sm:flex-row sm:space-x-4 ${
            isDropdownOpen ? 'space-y-3 p-3 bg-white border border-gray-200 rounded-xl shadow-sm mt-2' : ''
          } sm:space-y-0`}
        >
          {activeRecipes.map((recipe, index) => {
            const recipeProgress =
              ((recipe.currentStep + 1) / recipe.instructions.length) * 100;
            const isCurrentRecipe = index === currentRecipeIndex;
            const isCompleted =
              recipe.currentStep === recipe.instructions.length - 1 &&
              recipe.completedSteps.includes(recipe.currentStep);

            return (
              <button
                key={recipe.id}
                onClick={() => {
                  goToRecipe(index);
                  setIsDropdownOpen(false); // Close dropdown on selection (mobile)
                }}
                className={`group relative overflow-hidden min-w-[220px] max-w-xs p-0 rounded-2xl border transition-all duration-300 text-left shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#87A96B]
                ${
                  isCurrentRecipe
                    ? 'border-[#87A96B] scale-[1.02]'
                    : 'border-gray-100'
                }`}
              >
                {/* Image */}
                <div className="h-24 w-full bg-gray-200 relative">
                  <img
                    src={
                            recipe.imgUrlArray.find(x => x.includes('/recipe')) ??
                            recipe.imgUrlArray.find(x => x.includes('/suggestion')) ??
                            "/img/placeholder-food.jpg"
                          }
                    alt={recipe.title}
                    className="object-cover w-full h-full rounded-t-2xl"
                  />
                  {isCompleted && (
                    <div className="absolute top-2 right-2 bg-[#87A96B] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow">
                      ✔ Terminé
                    </div>
                  )}
                </div>

                <div className="p-3 bg-white rounded-b-2xl">
                  {/* Titre */}
                  <h3
                    className={`text-sm font-semibold mb-1 truncate transition-colors ${
                      isCurrentRecipe
                        ? 'text-[#E67E22]'
                        : 'text-[#2C3E50] group-hover:text-[#E67E22]'
                    }`}
                  >
                    {recipe.title}
                  </h3>

                  {/* Infos avec icônes */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3 text-[#2C3E50]" />
                      <span>{recipe.instructions.length} étapes</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Flame className="w-3 h-3 text-[#E67E22]" />
                      <span>{Math.round(recipeProgress)}%</span>
                    </div>
                  </div>

                  {/* Barre de progression */}
                  <div className="w-full h-1.5 bg-gray-100 rounded-full shadow-inner mb-2">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ease-in-out ${
                        isCompleted
                          ? 'bg-[#87A96B]'
                          : isCurrentRecipe
                          ? 'bg-[#87A96B]'
                          : 'bg-[#E67E22]'
                      }`}
                      style={{ width: `${recipeProgress}%` }}
                    ></div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
const RecipeRender = (props) => {
  const [adults, setAdults] = useState(null);
  const [teens, setTeens] = useState(null);
  const [children, setChildren] = useState(null);
  const [servings, setServings] = useState(null);
  const [progress, setProgress] = useState(0);
  const [recipe, setRecipe] = useState(null);
  // Variables pour la recette actuelle
  const [currentStep, setCurrentStep] = useState(2);
  const [isCompleted, setIsCompleted] = useState(false);
  const [currentRecipeTimers, setCurrentRecipeTimers] = useState([]);
const [showIngredients, setShowIngredients] = useState(false); // dans ton composant parent
  const [showFinishModal, setShowFinishModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setServings(props.servings);
  }, [props.servings]);

  // Détection de la fin de recette
  useEffect(() => {
    if (isCompleted) {
      setShowFinishModal(true);
      try {
        var newRecipe = recipe;
        newRecipe.updatedAt = new Date();
        newRecipe.status = "finished";
        api.post("/api/meals/update-recipe", { recipe: newRecipe });
        api.post(`/api/users/update-user-list`, {
          recipe: newRecipe,
          typeList: "finished",
        });
      } catch (error) {
        console.error("Erreur:", error);
        alert("Une erreur est survenue lors de la sauvegarde de la recette.");
      }
    }
  }, [isCompleted]); // ne se déclenche que quand isCompleted passe à true

  useEffect(() => {
    if (props.recipe && props.activeRecipes.length > 0) {
      setRecipe(props.recipe);
    }
  }, [props.recipe]);

  useEffect(() => {
    if (recipe) {
      setCurrentStep(recipe.instructions[recipe.currentStep]);
      setProgress(
        ((recipe.currentStep + 1) / recipe.instructions.length) * 100
      );
      setIsCompleted(
        recipe.currentStep === recipe.instructions.length - 1 &&
          recipe.completedSteps.includes(recipe.currentStep)
      );
    }
  }, [recipe?.currentStep, recipe?.completedSteps]);

  useEffect(() => {
    if (recipe) {
      // Timers de la recette actuelle
      setCurrentRecipeTimers(
        props.timers.filter((timer) => timer.recipeId === recipe.id)
      );
    }
  }, [recipe, props.timers]);

  // Fonction pour ajuster les quantités selon le nombre de personnes
  const adjustQuantityForServings = (
    originalQuantity,
    currentServings,
    originalServings
  ) => {
    const numericQuantity = parseFloat(originalQuantity);
    if (isNaN(numericQuantity)) return originalQuantity;

    const adjustedQuantity =
      (numericQuantity * currentServings) / originalServings;

    if (adjustedQuantity < 1) {
      return adjustedQuantity.toFixed(1);
    } else if (adjustedQuantity < 10) {
      return Math.round(adjustedQuantity * 10) / 10;
    } else {
      return Math.round(adjustedQuantity);
    }
  };
  
  return (
    <>
      {/* Modal de fin de recette */}
      {showFinishModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-xs w-full flex flex-col items-center">
            <div className="text-4xl mb-4">🎉</div>
            <h2 className="text-xl font-bold text-[#87A96B] mb-2 text-center">Bravo !</h2>
            <p className="text-[#2C3E50] text-center mb-6">
              Vous avez terminé la recette.<br />
              Vous allez être invité à la noter dans quelques heures.
            </p>
            <button
              className="px-6 py-2 rounded-full bg-[#87A96B] text-white font-semibold shadow hover:bg-[#87A96B]/90 transition"
              onClick={() => navigate("/dashboard")}
            >
              OK
            </button>
          </div>
        </div>
      )}
      {props.toDisplay && recipe && (
        <div className="max-w-4xl mx-auto p-4">
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#2C3E50] text-white flex justify-around items-center p-2">
            <div className="group relative flex-1 text-center">
              <div className="flex flex-col items-center cursor-pointer hover:text-[#E67E22] transition-colors">
                <Timer className="w-5 h-5 mb-1" />
                <span className="text-xs">Timers ({currentRecipeTimers.length})</span>
              </div>
              <div className="absolute bottom-full left-0 w-screen px-1 py-3 md:px-3 bg-white text-[#2C3E50] border-t rounded-t-lg shadow-lg z-50 hidden group-hover:block">
                {currentRecipeTimers.length === 0 ? (
                  <p className="text-center text-sm py-2">
                    Aucun timer actif.
                    <br />
                    Lancez un timer depuis les étapes.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {currentRecipeTimers.map((timer) => (
                      <props.CompactTimerCard key={timer.id} timer={timer} />
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="group relative flex-1 text-center">
              <div className="flex flex-col items-center cursor-pointer hover:text-[#E67E22] transition-colors">
                <Plus className="w-5 h-5 mb-1" />
                <span className="text-xs">Ajouter</span>
              </div>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-screen px-1 py-3 md:px-3 bg-white text-[#2C3E50] border-t rounded-t-lg shadow-lg z-50 hidden group-hover:block">
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {props.readyToCookRecipes
                    .filter(
                      (recipe) =>
                        !props.activeRecipes.some((active) => active.title === recipe.title)
                    )
                    .map((recipe) => (
                      <button
                        key={recipe.id}
                        onClick={() => props.addRecipe(recipe)}
                        className="block w-full text-left p-2 bg-gray-50 rounded border hover:bg-[#87A96B]/10"
                      >
                        <div className="font-medium text-sm text-[#2C3E50]">{recipe.title}</div>
                        <div className="text-xs text-gray-600">
                          {recipe.instructions.length} étapes • {recipe.prepTime} min
                        </div>
                      </button>
                    ))}
                </div>
              </div>
            </div>

            <div className="group relative flex-1 text-center">
              <div className="flex flex-col items-center cursor-pointer hover:text-[#E67E22] transition-colors">
                <ChefHat className="w-5 h-5 mb-1" />
                <span className="text-xs">Recettes</span>
              </div>
              <div className="absolute bottom-full right-0 w-screen px-1 py-3 md:px-3 bg-white text-[#2C3E50] border-t rounded-t-lg shadow-lg z-50 max-h-60 overflow-y-auto hidden group-hover:block">
                <div className="space-y-2">
                  {props.activeRecipes
                    .filter((_, index) => index !== props.currentRecipeIndex)
                    .map((recipe) => {
                      const realIndex = props.activeRecipes.findIndex((r) => r.id === recipe.id);
                      const recipeProgress = ((recipe.currentStep + 1) / recipe.instructions.length) * 100;
                      const isCompleted =
                        recipe.currentStep === recipe.instructions.length - 1 &&
                        recipe.completedSteps.includes(recipe.currentStep);
                      return (
                        <button
                          key={recipe.id}
                          onClick={() => props.goToRecipe(realIndex)}
                          className="block w-full text-left p-3 bg-gray-50 rounded border hover:bg-[#87A96B]/10"
                        >
                          <div className="flex justify-between mb-1 text-sm font-medium text-[#2C3E50]">
                            {recipe.title}
                            {isCompleted && <CheckCircle className="w-4 h-4 text-[#87A96B]" />}
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1">
                            <div
                              className={`h-1 rounded-full ${isCompleted ? "bg-[#87A96B]" : "bg-[#E67E22]"}`}
                              style={{ width: `${recipeProgress}%` }}
                            ></div>
                          </div>
                        </button>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
              <div>
                <div className="lg:col-span-3">
                  <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden transition-all hover:shadow-2xl shadow-lg">
              <div
                className="relative p-8 bg-gradient-to-br from-[#87A96B] via-[#87A96B] to-[#2C3E50] mb-10"
                style={{
                  backgroundImage:
                    `url(${recipe.imgUrlArray.find(x => x.includes('/recipe')) ??
                    recipe.imgUrlArray.find(x => x.includes('/suggestion')) ??
                    "/img/placeholder-food.jpg"})`,
                  backgroundBlendMode: "multiply",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center space-x-4 backdrop-blur-md bg-black/20 p-4 rounded-2xl border border-white/30">
                <div>
                  <h1 className="text-3xl font-bold text-white drop-shadow-lg tracking-tight">
                    {recipe.title}
                  </h1>
                  <div className="flex items-center space-x-3 mt-2">
                    <span className="text-sm text-white bg-white/25 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/30 font-medium">
                      {recipe.dishType}
                    </span>
                    <span className="text-sm text-white/90 flex items-center font-medium">
                      <Clock className="w-4 h-4 mr-1.5" />{" "}
                      {recipe.prepTime} min
                    </span>
                  </div>
                </div>
                    </div>
                  </div>

                  {/* Chemin des étapes + barre de progression */}
                  <div>
                    {/* Desktop/tablette : chemin des étapes et barre de progression */}
                    <div className="mb-3 overflow-x-auto pb-1 hidden sm:block">
                      <div className="flex items-start space-x-4 bg-black/20 backdrop-blur-sm p-1.5 rounded-xl border border-white/20">
                {recipe.instructions.map((step, index) => {
                          const isCompleted =
                            recipe.completedSteps.includes(index);
                          const isCurrent = index === recipe.currentStep;
                          const circleClasses = `
      w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
      transition-all duration-300 shadow-lg
      ${
        isCompleted
          ? "bg-white text-[#87A96B] shadow-xl transform scale-105"
          : isCurrent
          ? "bg-[#E67E22] text-white ring-2 ring-white/80 animate-pulse shadow-2xl"
          : index < recipe.currentStep
          ? "bg-[#E67E22]/70 text-white shadow-md"
          : "bg-white/20 text-white/80 backdrop-blur-sm"
      }`;

                          return (
                            <div
                              key={index}
                              className="flex flex-col items-center flex-shrink-0"
                            >
                              <div className={circleClasses}>
                                {isCompleted ? (
                                  <CheckCircle className="w-4 h-4" />
                                ) : (
                                  index + 1
                                )}
                              </div>
                              {index < recipe.instructions.length - 1 && (
                                <div
                                  className={`w-px h-4 my-1 transition-all duration-300 ${
                                    isCompleted
                                      ? "bg-white shadow-sm"
                                      : "bg-white/50"
                                  }`}
                                />
                              )}
                              <span
                                className={`
      mt-1 text-xs font-medium text-center px-2 py-1 rounded-full backdrop-blur-sm
      ${isCurrent ? "text-white font-bold bg-white/20" : "text-white/80"}
      `}
                              >
                                Étape {index + 1}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {/* Mobile : texte simple */}
                    <div className="mb-3 px-2 py-2 sm:hidden flex items-center justify-center">
                      <span className="text-white font-bold text-base bg-black/50 rounded-full px-4 py-2 shadow-md">
                        Étape {recipe.currentStep + 1} / {recipe.instructions.length}
                      </span>
                    </div>
                                          {/* Barre de progression */}
                      <div className="w-full bg-black/30 rounded-full h-4 overflow-hidden backdrop-blur-sm border border-white/20 mt-2">
                        <div
                          className="bg-[#E67E22] h-3 rounded-full transition-all duration-500 flex items-center justify-end pr-4 relative shadow-lg"
                          style={{ width: `${progress}%` }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer rounded-full"></div>
                          {progress > 15 && (
                            <span className="text-white text-xs font-bold drop-shadow-sm relative z-10">
                              {Math.round(progress)}%
                            </span>
                          )}
                        </div>
                      </div>
                  </div>
                </div>
              </div>

                {/* Contenu principal amélioré */}
                <div className="sm:p-8 space-y-8">
                  <ServingsAdjuster
                    servings={servings}
                    initialAdults={adults}
                    initialTeens={teens}
                    initialChildren={children}
                    changeAdults={setAdults}
                    changeTeens={setTeens}
                    changeChildren={setChildren}
                    onServingsChange={setServings}
                    originalServings={2}
                  />

                  {/* Ingrédients avec nouvelle palette */}
                  {currentStep.allIngredientUsed &&
                    currentStep.allIngredientUsed.length > 0 && (
                    <div className="bg-gradient-to-br from-[#E67E22]/10 to-[#E67E22]/5 rounded-2xl border border-[#E67E22]/30 shadow-lg overflow-hidden">
                      {/* Header - toujours visible */}
                      <div
                        className="flex justify-between items-center sm:p-6 p-4 cursor-pointer sm:cursor-default"
                        onClick={() => setShowIngredients(!showIngredients)}
                      >
                        <h4 className="text-base sm:text-lg font-bold text-[#2C3E50] flex items-center">
                          <div className="bg-[#E67E22]/20 p-1.5 sm:p-2 rounded-xl mr-3">
                            <Circle className="w-5 h-5 sm:w-6 sm:h-6 text-[#E67E22]" />
                          </div>
                          Ingrédients nécessaires
                        </h4>
                        <div className="sm:hidden text-[#2C3E50] font-bold text-xl">
                          {showIngredients ? '−' : '+'}
                        </div>
                      </div>

                      {/* Liste des ingrédients */}
                      <div className={`${showIngredients ? 'block' : 'hidden'} sm:block px-4 sm:px-6 pb-4 sm:pb-6`}>
                        <div className="grid gap-3 sm:gap-4">
                          {currentStep.allIngredientUsed.map((ingredient, index) => {
                            // Calcul spécial si unité en pourcentage: appliquer le % sur la quantité initiale de la recette
                            const roundDisplay = (val) => {
                              const num = parseFloat(val);
                              if (isNaN(num)) return val;
                              if (num < 1) return num.toFixed(2);
                              if (num < 10) return Math.round(num * 10) / 10;
                              return Math.round(num);
                            };

                            let qtyToShow;
                            let unitToShow;
                            if (ingredient.unity === '%' && Array.isArray(recipe.ingredients)) {
                              const baseIng = recipe.ingredients.find((rIng) => rIng.name === ingredient.name);
                              const baseQty = baseIng ? parseFloat(baseIng.quantity) : NaN;
                              const perc = parseFloat(ingredient.quantityUsed);
                              if (!isNaN(baseQty) && !isNaN(perc)) {
                                const adjBase = parseFloat(
                                  adjustQuantityForServings(
                                    baseQty,
                                    servings,
                                    recipe.originalServings || 2
                                  )
                                );
                                const computed = (adjBase * perc) / 100;
                                qtyToShow = roundDisplay(computed);
                                unitToShow = baseIng?.unity || '';
                              }
                            }

                            if (qtyToShow === undefined) {
                              qtyToShow = adjustQuantityForServings(
                                ingredient.quantityUsed,
                                servings,
                                recipe.originalServings || 2
                              );
                              unitToShow = ingredient.unity;
                            }

                            return (
                              <div
                                key={index}
                                className="flex items-center justify-between bg-white p-3 sm:p-4 rounded-xl border border-[#E67E22]/20 shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5"
                              >
                                <span className="font-medium sm:font-semibold text-[#2C3E50] flex items-center text-sm sm:text-base">
                                  <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#E67E22] mr-2 sm:mr-3 shadow-sm"></span>
                                  {ingredient.name}
                                </span>
                                <span className="text-[#2C3E50] font-bold bg-[#E67E22]/10 px-2 sm:px-3 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm shadow-sm">
                                  {qtyToShow} {unitToShow}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    )}

                  <div className="flex justify-between items-center pt-2 pb-6 border-b border-gray-100">
                    <button
                      onClick={() => props.previousStep(recipe.id)}
                      disabled={recipe.currentStep === 0}
                      className={`px-4 py-2 sm:px-8 sm:py-4 rounded-full transition-all flex items-center font-medium text-sm sm:text-base ${
                        recipe.currentStep === 0
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-[#2C3E50] text-white hover:bg-[#2C3E50]/90 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                      }`}
                    >
                      <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      Précédent
                    </button>

                    <button
                      onClick={() => props.nextStep(recipe.id)}
                      className="px-4 py-2 sm:px-8 sm:py-4 bg-[#87A96B] text-white rounded-full hover:bg-[#87A96B]/90 hover:shadow-xl transition-all flex items-center shadow-lg font-medium text-sm sm:text-base transform hover:-translate-y-0.5 hover:scale-105"
                    >
                      {recipe.currentStep === recipe.instructions.length - 1 ? (
                        <>
                          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                          Terminer
                        </>
                      ) : (
                        <>
                          Suivant <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                        </>
                      )}
                    </button>
                  </div>
                  
                  {/* Instructions avec nouvelle palette */}
                  <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-lg">
                    <div className="flex items-center mb-5">
                      <div className="w-10 h-10 rounded-full bg-[#87A96B]/20 text-[#87A96B] flex items-center justify-center mr-4 font-bold text-lg shadow-md">
                        {recipe.currentStep + 1}
                      </div>
                      <h4 className="font-bold text-[#2C3E50] text-base sm:text-lg md:text-xl">
                        Instructions
                      </h4>
                    </div>
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-5 rounded-xl border-l-4 border-[#87A96B] shadow-inner">
                      <p className="text-[#2C3E50] leading-relaxed text-sm sm:text-base md:text-lg">
                        <ExplanationWithTimes
                          text={currentStep.step}
                          timings={currentStep.ListOfTimingsInStep}
                          servings={servings}
                          originalServings={recipe.originalServings || 2}
                          ingredients={recipe.ingredients}
                          onStartTimer={({ label, duration }) =>
                            props.startTimerFromText
                              ? props.startTimerFromText(recipe.id, duration, label)
                              : props.createTimer(recipe.id, duration, label)
                          }
                        />
                      </p>
                    </div>
                    {currentStep.tips && (
                      <div className="mt-5 sm:p-5 p-4 bg-[#87A96B]/10 border border-[#87A96B]/30 rounded-xl flex items-start shadow-sm">
                        <span className="text-xl sm:text-2xl mr-3">💡</span>
                        <div>
                          <p className="text-sm sm:text-base font-semibold text-[#87A96B]">
                            Conseil de YAQAM
                          </p>
                          <p className="text-sm sm:text-base text-[#2C3E50] mt-2 leading-relaxed">
                            {currentStep.tips}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Timers avec nouvelle palette */}
                  {currentStep.ListOfTimingsInStep && currentStep.ListOfTimingsInStep.length > 0 && (
                    <div className="p-6 bg-[#2C3E50]/5 rounded-2xl border border-[#2C3E50]/20 shadow-lg">
                      <h4 className="text-lg font-bold text-[#2C3E50] mb-5 flex items-center">
                        <div className="bg-[#2C3E50]/10 p-2 rounded-xl mr-3">
                          <Clock className="w-6 h-6 text-[#2C3E50]" />
                        </div>
                        Minuteries
                      </h4>
                      <div className="grid gap-3">
                        {currentStep.ListOfTimingsInStep.map((timing, index) => {
                          const additionalTimePerPersonInSecond = Number(timing?.adjustmentFactor?.additionalTimePerPersonInSecond) || 0;
                          const maxAdd = timing?.adjustmentFactor?.max;
                          const diff = Math.max(0, (servings ?? 0) - (recipe.originalServings || 2));
                          const add = additionalTimePerPersonInSecond > 0 ? additionalTimePerPersonInSecond * diff : 0;
                          const capped = typeof maxAdd === 'number' && maxAdd >= 0 ? Math.min(add, maxAdd) : add;
                          const adjustedDuration = Math.max(0, (Number(timing.duration) || 0) + capped);
                          const alreadyExists = props.timers.some(
                            (t) => t.recipeId === recipe.id && t.label === timing.label && !t.isFinished
                          );
                          return (
                            <button
                              key={index}
                              onClick={() =>
                                props.createTimer(
                                  recipe.id,
                                  adjustedDuration,
                                  timing.label
                                )
                              }
                              disabled={alreadyExists}
                              className="w-full text-left p-4 bg-white rounded-xl border border-[#2C3E50]/10 hover:bg-[#2C3E50]/5 transition-all flex items-center justify-between shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                            >
                              <span className="flex items-center text-[#2C3E50] font-medium">
                                <Clock className="w-5 h-5 mr-3 text-[#2C3E50]" />
                                {timing.label}
                              </span>
                              <span className="text-[#2C3E50] font-bold bg-[#2C3E50]/10 px-3 py-2 rounded-full shadow-sm">
                                {formatDurationHuman(adjustedDuration)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Bouton retirer recette avec nouvelle palette */}
                  <div className="flex justify-center">
                    <button
                      onClick={() => props.removeRecipe(recipe.id)}
                      className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 px-4 py-2 rounded-full transition-all duration-200 shadow-sm hover:shadow-md hover:bg-red-100"
                    >
                      <Trash className="w-5 h-5" />
                      <span className="font-medium">Retirer la recette</span>
                    </button>
                  </div>
<div className="pb-8"></div> {/* Ajoute ce padding bas juste après */}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const CookingAssistantPage = () => {
  const [activeRecipes, setActiveRecipes] = useState([]);
  const [readyToCookRecipes, setReadyToCookRecipes] = useState([]);
  const [currentRecipeIndex, setCurrentRecipeIndex] = useState(0);
  const [currentRecipe, setCurrentRecipe] = useState(null);
  const [timers, setTimers] = useState([]);
  const [nextRecipeId, setNextRecipeId] = useState(1);
  const [nextTimerId, setNextTimerId] = useState(1);
  const [showRecipeSelector, setShowRecipeSelector] = useState(false);
  const [showAllTimers, setShowAllTimers] = useState(false);
  const [highlightTimerId, setHighlightTimerId] = useState(null);
  const alarmIntervalsRef = useRef({});
  const [batchMode, setBatchMode] = useState(false);
  const [batchPlan, setBatchPlan] = useState(null);
  const [showBatchPlanner, setShowBatchPlanner] = useState(false);
  //stocker les IDs sélectionnés dans la section d'accueil
  const [initialSelection, setInitialSelection] = useState([]);
  const audioRef = useRef(null);

  const seo = (
    <Seo
      title="Assistant de cuisine"
      description="Suivez vos recettes pas à pas, gérez vos timers et cuisinez sereinement avec l'assistant YAQAM."
      breadcrumb
    />
  );

  //On définit readyToCookRecipes
  useEffect(() => {
    const fetchReadyToCookRecipes = async () => {
      try {
        const response = await api.post(`/api/meals/recipes?`, {
          status: "readyToCook",
        });
        const data = response.data;
        if (data.success === false) {
          setReadyToCookRecipes([]);
          return;
        }
        console.log("Recettes à faire récupérées:", data.recipes);
        // Trier de la plus récente à la moins récente (updatedAt puis createdAt)
        const sortedByRecent = [...data.recipes].sort((a, b) => {
          const da = new Date(a.updatedAt || a.createdAt || 0);
          const db = new Date(b.updatedAt || b.createdAt || 0);
          return db - da;
        });
        setReadyToCookRecipes(sortedByRecent);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des recettes à faire:",
          error
        );
      }
    };
    fetchReadyToCookRecipes();
  }, []);

  useEffect(() => {
    setCurrentRecipe(activeRecipes[currentRecipeIndex]);
  }, [activeRecipes, currentRecipeIndex]);

  useEffect(() => {
    console.log("boom");
    console.log(currentRecipe);
  }, [currentRecipe]);
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes}min`;
  };

  // Son de notification
  useEffect(() => {
    // court bip
    audioRef.current = new Audio(
      "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAcTXrTp66hVFApGn+DyvmYpASOC0/PLfC4HJoHQ8tqJOAgbaLrv45lMEAxt4w=="
    );
  }, []);

  // Gestion des timers
  useEffect(() => {
    const interval = setInterval(() => {
      setTimers((prevTimers) =>
        prevTimers.map((timer) => {
          if (timer.isRunning && timer.timeLeft > 0) {
            const newTimeLeft = timer.timeLeft - 1;
            if (newTimeLeft === 0) {
              if (audioRef.current) {
                // Alarme continue: boucle audio jusqu'à arrêt utilisateur (compatible mobile)
                try {
                  const src = audioRef.current.src;
                  const alarm = new Audio(src);
                  alarm.loop = true;
                  // iOS/Android: baisser volume pour confort
                  alarm.volume = 0.8;
                  alarm.play().catch(() => {});
                  // Conserver la référence pour pouvoir arrêter
                  const mapI = alarmIntervalsRef.current || {};
                  // Si une ancienne alarme existe pour ce timer, l'arrêter
                  if (mapI[timer.id]) {
                    clearInterval(mapI[timer.id]);
                    delete mapI[timer.id];
                  }
                  // Stocker l'objet audio dans la même map (clé identique)
                  mapI[timer.id] = alarm;
                  alarmIntervalsRef.current = mapI;
                } catch (e) {
                  audioRef.current.play().catch(() => {});
                }
              }
              // Ouvrir automatiquement le panneau des timers et mettre en évidence le timer fini
              setShowAllTimers(true);
              setHighlightTimerId(timer.id);
              setTimeout(() => {
                const elCard = document.getElementById(`timer-${timer.id}`);
                if (elCard && typeof elCard.scrollIntoView === 'function') {
                  elCard.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
                }
                setTimeout(() => setHighlightTimerId(null), 2000);
              }, 80);
              return {
                ...timer,
                timeLeft: 0,
                isRunning: false,
                isFinished: true,
              };
            }
            return { ...timer, timeLeft: newTimeLeft };
          }
          return timer;
        })
      );
    }, 1000);

    return () => {
      clearInterval(interval);
      // Nettoyage des alarmes si le composant est démonté
      const map = alarmIntervalsRef.current || {};
      Object.values(map).forEach((val) => {
        if (typeof val === 'number') clearInterval(val);
        else if (val && typeof val.pause === 'function') { try { val.pause(); } catch {} }
      });
      alarmIntervalsRef.current = {};
    };
  }, []);

  // Ajouter une nouvelle recette (avec enrichissement depuis l'API pour inclure les tips YAQAM)
  const addRecipe = async (recipeTemplate) => {
    try {
      let detailed = null;
      try {
        const resp = await api.get(`/api/meals/recipe/${recipeTemplate.id || recipeTemplate._id}`);
        detailed = resp?.data?.data?.recipe || null;
      } catch (e) {
        console.warn('Fallback to template: unable to fetch detailed recipe', e);
      }

      const base = detailed || recipeTemplate;
      const newRecipe = {
        ...recipeTemplate,
        // On privilégie les instructions détaillées (avec tips) si disponibles
        instructions: base.instructions || recipeTemplate.instructions || [],
        imgUrlArray: base.imgUrlArray || recipeTemplate.imgUrlArray || [],
        title: base.title || recipeTemplate.title,
        prepTime: base.prepTime || recipeTemplate.prepTime,
        complexity: base.complexity || recipeTemplate.complexity,
        currentStep: 0,
        completedSteps: [],
        startedAt: new Date(),
        currentServings: base.originalServings || recipeTemplate.originalServings || 2,
      };
      console.log('addRecipe (detailed)', newRecipe);
      setActiveRecipes((prev) => {
        const next = [...prev, newRecipe];
        setCurrentRecipeIndex(next.length - 1); // Se positionner sur la nouvelle recette
        return next;
      });
    } catch (err) {
      console.error('Error adding recipe to session:', err);
      // Fallback minimal si tout échoue
      const newRecipe = {
        ...recipeTemplate,
        currentStep: 0,
        completedSteps: [],
        startedAt: new Date(),
        currentServings: recipeTemplate.originalServings || 2,
      };
      setActiveRecipes((prev) => {
        const next = [...prev, newRecipe];
        setCurrentRecipeIndex(next.length - 1);
        return next;
      });
    }
  };

  // Supprimer une recette
  const removeRecipe = (recipeId) => {
    const recipeIndex = activeRecipes.findIndex((r) => r.id === recipeId);
    setActiveRecipes((prev) => prev.filter((recipe) => recipe.id !== recipeId));
    setTimers((prev) => prev.filter((timer) => timer.recipeId !== recipeId));

    // Ajuster l'index actuel si nécessaire
    if (recipeIndex === currentRecipeIndex && activeRecipes.length > 1) {
      setCurrentRecipeIndex(Math.max(0, currentRecipeIndex - 1));
    } else if (activeRecipes.length === 1) {
      setCurrentRecipeIndex(0);
    }
  };

  const goToPreviousRecipe = () => {
    if (currentRecipeIndex > 0) {
      setCurrentRecipeIndex(currentRecipeIndex - 1);
    }
  };

  const goToNextRecipe = () => {
    if (currentRecipeIndex < activeRecipes.length - 1) {
      setCurrentRecipeIndex(currentRecipeIndex + 1);
    }
  };

  const goToRecipe = (index) => {
    setCurrentRecipeIndex(index);
    setShowRecipeSelector(false);
  };

  // Ajuster le nombre de personnes pour une recette avec useCallback
  const updateServings = (recipeId, newServings) => {
    console.log("recipeId");
    console.log(recipeId);
  };
  // Pas de dépendances car la fonction est stable

  // Navigation dans les étapes
  const nextStep = (recipeId) => {
    console.log(activeRecipes);
    setActiveRecipes((prev) =>
      prev.map((r) => {
        if (r.id !== recipeId) return r; // on ne modifie pas les autres
        return {
          ...r,
          // on ajoute l'étape en cours dans completedSteps
          completedSteps: [...r.completedSteps, r.currentStep],
          // on incrémente currentStep (sans dépasser la dernière étape)
          currentStep: Math.min(r.currentStep + 1, r.instructions.length - 1),
        };
      })
    );
  };

  const previousStep = (recipeId) => {
    setActiveRecipes((prev) =>
      prev.map((recipe) => {
        if (recipe.id === recipeId) {
          const newCompletedSteps = recipe.completedSteps.filter(
            (step) => step !== recipe.currentStep - 1
          );
          return {
            ...recipe,
            currentStep: Math.max(recipe.currentStep - 1, 0),
            completedSteps: newCompletedSteps,
          };
        }
        return recipe;
      })
    );
  };

  // Démarrage d'une minuterie depuis le texte (balises Time)
  const startTimerFromText = (recipeId, duration, label) => {
    const exists = timers.some(
      (t) => t.recipeId === recipeId && t.label === label && !t.isFinished
    );
    if (exists) {
      // Ouvrir le panel et faire défiler vers lui
      setShowAllTimers(true);
      setTimeout(() => {
        const match = timers.find((t) => t.recipeId === recipeId && t.label === label && !t.isFinished);
        if (match) {
          setHighlightTimerId(match.id);
          const elCard = document.getElementById(`timer-${match.id}`);
          if (elCard && typeof elCard.scrollIntoView === 'function') {
            elCard.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
          }
          setTimeout(() => setHighlightTimerId(null), 1500);
        } else {
          const el = document.getElementById('floating-timers-panel');
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
          }
        }
      }, 50);
      return;
    }
    const newId = nextTimerId;
    createTimer(recipeId, duration, label);
    setShowAllTimers(true);
    setTimeout(() => {
      setHighlightTimerId(newId);
      const elCard = document.getElementById(`timer-${newId}`);
      if (elCard && typeof elCard.scrollIntoView === 'function') {
        elCard.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
      setTimeout(() => setHighlightTimerId(null), 1500);
    }, 80);
  };

  // Gestion des timers
  const createTimer = (recipeId, duration, label) => {
    // Empêcher la création multiple d'une même minuterie (même recette + même label) tant qu'elle n'est pas terminée
    const exists = timers.some(
      (t) => t.recipeId === recipeId && t.label === label && !t.isFinished
    );
    if (exists) {
      window.alert(`La minuterie "${label}" est déjà présente pour cette recette.`);
      return;
    }

    const newTimer = {
      id: nextTimerId,
      recipeId,
      duration,
      timeLeft: duration,
      label,
      isRunning: true,
      isFinished: false,
      createdAt: new Date(),
    };
    setTimers((prev) => [...prev, newTimer]);
    setNextTimerId((prev) => prev + 1);
  };

  const toggleTimer = (timerId) => {
    // arrêter l'alarme si elle tourne
    const map = alarmIntervalsRef.current || {};
    const val = map[timerId];
    if (val) {
      if (typeof val === 'number') clearInterval(val);
      else if (val && typeof val.pause === 'function') { try { val.pause(); val.currentTime = 0; val.loop = false; } catch {} }
      delete map[timerId];
    }
    setTimers((prev) =>
      prev.map((timer) =>
        timer.id === timerId
          ? { ...timer, isRunning: !timer.isRunning, isFinished: false }
          : timer
      )
    );
  };

  const resetTimer = (timerId) => {
    const map = alarmIntervalsRef.current || {};
    const val = map[timerId];
    if (val) {
      if (typeof val === 'number') clearInterval(val);
      else if (val && typeof val.pause === 'function') { try { val.pause(); val.currentTime = 0; val.loop = false; } catch {} }
      delete map[timerId];
    }
    setTimers((prev) =>
      prev.map((timer) =>
        timer.id === timerId
          ? {
              ...timer,
              timeLeft: timer.duration,
              isRunning: false,
              isFinished: false,
            }
          : timer
      )
    );
  };

  const deleteTimer = (timerId) => {
    const map = alarmIntervalsRef.current || {};
    const val = map[timerId];
    if (val) {
      if (typeof val === 'number') clearInterval(val);
      else if (val && typeof val.pause === 'function') { try { val.pause(); val.currentTime = 0; val.loop = false; } catch {} }
      delete map[timerId];
    }
    setTimers((prev) => prev.filter((timer) => timer.id !== timerId));
  };

  // Algorithme d'optimisation pour le batch cooking
  const generateBatchPlan = (selectedRecipes) => {
    if (selectedRecipes.length === 0) return null;

    // Calculer la durée totale de chaque recette
    const recipesWithDuration = selectedRecipes.map((recipe) => {
      const totalDuration = recipe.instructions.reduce((sum, instruction) => {
        const stepDuration = instruction.ListOfTimingsInStep
          ? instruction.ListOfTimingsInStep.reduce(
              (stepSum, timing) => stepSum + timing.duration,
              0
            )
          : 0;
        return sum + totalDuration;
      }, 0);

      return {
        ...recipe,
        totalDuration,
        steps: recipe.instructions.map((instruction, index) => ({
          ...instruction,
          stepIndex: index,
          duration: instruction.ListOfTimingsInStep
            ? instruction.ListOfTimingsInStep.reduce(
                (sum, timing) => sum + timing.duration,
                0
              )
            : 0,
        })),
      };
    });

    // Trier par durée totale décroissante (plus longs en premier)
    const sortedRecipes = recipesWithDuration.sort(
      (a, b) => b.totalDuration - a.totalDuration
    );

    // Générer le planning optimisé
    let currentTime = 0;
    const timeline = [];
    const recipeSchedules = {};

    sortedRecipes.forEach((recipe, recipeIndex) => {
      let recipeStartTime = currentTime;
      let lastStepEndTime = recipeStartTime;

      recipeSchedules[recipe.id] = {
        recipe,
        startTime: recipeStartTime,
        steps: [],
        estimatedEndTime: 0,
      };

      recipe.steps.forEach((step, stepIndex) => {
        const stepStartTime = lastStepEndTime;
        const stepEndTime = stepStartTime + step.duration;

        const scheduledStep = {
          ...step,
          recipeId: recipe.id,
          recipeTitle: recipe.title,
          stepIndex,
          startTime: stepStartTime,
          endTime: stepEndTime,
          canParallelize: step.duration > 300, // Étapes > 5min peuvent être parallélisées
        };

        recipeSchedules[recipe.id].steps.push(scheduledStep);
        timeline.push(scheduledStep);

        lastStepEndTime = stepEndTime;
      });

      recipeSchedules[recipe.id].estimatedEndTime = lastStepEndTime;

      // Décaler le début de la prochaine recette si nécessaire
      if (recipeIndex < sortedRecipes.length - 1) {
        currentTime += Math.max(300, recipe.steps[0]?.duration || 0); // Minimum 5min de décalage
      }
    });

    // Optimiser les parallélisations
    const optimizedTimeline = optimizeParallelSteps(timeline);

    return {
      recipes: sortedRecipes,
      timeline: optimizedTimeline,
      schedules: recipeSchedules,
      totalDuration: Math.max(
        ...Object.values(recipeSchedules).map((s) => s.estimatedEndTime)
      ),
      estimatedSavings: calculateTimeSavings(sortedRecipes, optimizedTimeline),
    };
  };

  // Optimiser les étapes qui peuvent être faites en parallèle
  const optimizeParallelSteps = (timeline) => {
    const sortedTimeline = [...timeline].sort(
      (a, b) => a.startTime - b.startTime
    );
    const optimized = [];

    sortedTimeline.forEach((step) => {
      // Chercher des créneaux libres pour les étapes parallélisables
      if (step.canParallelize) {
        const overlappingSteps = optimized.filter(
          (existing) =>
            step.startTime < existing.endTime &&
            step.endTime > existing.startTime &&
            existing.recipeId !== step.recipeId
        );

        if (overlappingSteps.length === 0) {
          optimized.push({
            ...step,
            parallelWith: [],
            optimized: false,
          });
        } else {
          // Chercher un créneau libre
          let newStartTime = step.startTime;
          let foundSlot = false;

          for (let existing of overlappingSteps) {
            if (
              existing.endTime <= step.startTime ||
              existing.startTime >= step.endTime
            ) {
              foundSlot = true;
              break;
            }
          }

          optimized.push({
            ...step,
            startTime: newStartTime,
            endTime: newStartTime + step.duration,
            parallelWith: foundSlot
              ? overlappingSteps.map((s) => s.recipeId)
              : [],
            optimized: foundSlot,
          });
        }
      } else {
        optimized.push({
          ...step,
          parallelWith: [],
          optimized: false,
        });
      }
    });

    return optimized;
  };

  // Calculer le temps économisé
  const calculateTimeSavings = (recipes, optimizedTimeline) => {
    const totalSequentialTime = recipes.reduce(
      (sum, recipe) => sum + recipe.totalDuration,
      0
    );
    const optimizedTime = Math.max(
      ...optimizedTimeline.map((step) => step.endTime)
    );
    return Math.max(0, totalSequentialTime - optimizedTime);
  };

  // Planificateur de batch cooking
  const BatchPlanner = () => {
    const [selectedRecipeIds, setSelectedRecipeIds] = useState([]);
    const [generatedPlan, setGeneratedPlan] = useState(null);
    const recipesById = useMemo(() => {
      if (!generatedPlan?.recipes) return new Map();
      const map = new Map();
      generatedPlan.recipes.forEach((recipe) => {
        if (!recipe) return;
        const key = recipe.id != null ? String(recipe.id) : null;
        if (key) {
          map.set(key, recipe);
        }
      });
      return map;
    }, [generatedPlan]);

    const availableRecipes = readyToCookRecipes.filter(
      (recipe) => !activeRecipes.some((active) => active.title === recipe.title)
    );

    const handleRecipeToggle = (recipeId) => {
      setSelectedRecipeIds((prev) =>
        prev.includes(recipeId)
          ? prev.filter((id) => id !== recipeId)
          : [...prev, recipeId]
      );
    };

    const handleGeneratePlan = () => {
      const selectedRecipes = readyToCookRecipes.filter((recipe) =>
        selectedRecipeIds.includes(recipe.id)
      );
      const plan = generateBatchPlan(selectedRecipes);
      setGeneratedPlan(plan);
    };

    const handleStartBatchCooking = () => {
      if (generatedPlan) {
        // Ajouter toutes les recettes selon le plan
        generatedPlan.recipes.forEach((recipe, index) => {
          setTimeout(() => {
            addRecipe(recipe);
          }, index * 500); // Décalage pour l'animation
        });

        setBatchPlan(generatedPlan);
        setBatchMode(true);
        setShowBatchPlanner(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-96 overflow-y-auto">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-[#2C3E50]">
                  Mode Batch Cooking
                </h3>
                <p className="text-gray-600 text-sm">
                  Optimisez votre session de cuisine en parallélisant les
                  recettes
                </p>
              </div>
              <button
                onClick={() => setShowBatchPlanner(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6">
            {!generatedPlan ? (
              <>
                {/* Sélection des recettes */}
                <div className="mb-6">
                  <h4 className="font-semibold text-[#2C3E50] mb-3">
                    Choisissez vos recettes (2 minimum)
                  </h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    {availableRecipes.map((recipe) => (
                      <button
                        key={recipe.id}
                        onClick={() => handleRecipeToggle(recipe.id)}
                        className={`p-4 border-2 rounded-lg text-left transition-all ${
                          selectedRecipeIds.includes(recipe.id)
                            ? "border-[#87A96B] bg-[#87A96B]/10"
                            : "border-gray-200 hover:border-[#87A96B]/50 hover:bg-[#87A96B]/5"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-[#2C3E50]">
                            {recipe.title}
                          </h5>
                          {selectedRecipeIds.includes(recipe.id) && (
                            <CheckCircle className="w-5 h-5 text-[#87A96B]" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          {recipe.instructions.length} étapes •{" "}
                          {recipe.prepTime} min
                        </p>
                        <p className="text-xs text-gray-500">
                          {recipe.dishType}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Bouton de génération */}
                <div className="text-center">
                  <button
                    onClick={handleGeneratePlan}
                    disabled={selectedRecipeIds.length < 2}
                    className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                      selectedRecipeIds.length >= 2
                        ? "bg-[#87A96B] text-white hover:bg-[#87A96B]/90"
                        : "bg-gray-200 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    Générer le planning optimisé ({selectedRecipeIds.length}{" "}
                    recettes)
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Plan généré */}
                <div className="space-y-6">
                  {/* Résumé */}
                  <div className="bg-[#87A96B]/10 border border-[#87A96B]/30 rounded-lg p-4">
                    <h4 className="font-semibold text-[#87A96B] mb-2">
                      Planning optimisé généré
                    </h4>
                    <div className="grid md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-[#87A96B] font-medium">
                          Durée totale:
                        </span>
                        <br />
                        {formatDuration(generatedPlan.totalDuration)}
                      </div>
                      <div>
                        <span className="text-[#87A96B] font-medium">
                          Temps économisé:
                        </span>
                        <br />
                        {formatDuration(generatedPlan.estimatedSavings)}
                      </div>
                      <div>
                        <span className="text-[#87A96B] font-medium">
                          Recettes:
                        </span>
                        <br />
                        {generatedPlan.recipes.length} en parallèle
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div>
                    <h4 className="font-semibold text-[#2C3E50] mb-3">
                      Timeline des étapes
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {generatedPlan.timeline
                        .sort((a, b) => a.startTime - b.startTime)
                        .map((step, index) => {
                          const relatedRecipeId = step?.recipeId != null ? String(step.recipeId) : null;
                          const relatedRecipe = relatedRecipeId ? recipesById.get(relatedRecipeId) : null;
                          return (
                            <div
                              key={index}
                              className={`p-3 rounded border-l-4 ${
                                step.optimized
                                  ? "border-[#87A96B] bg-[#87A96B]/10"
                                  : "border-[#E67E22] bg-[#E67E22]/10"
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="font-medium text-sm text-[#2C3E50]">
                                    {step.recipeTitle}
                                  </span>
                                  <span className="text-gray-600 text-sm ml-2">
                                    - Étape {step.stepIndex + 1}
                                  </span>
                                </div>
                                <div className="text-right">
                                  <div className="text-xs text-gray-600">
                                    {formatDuration(step.startTime)} →{" "}
                                    {formatDuration(step.endTime)}
                                  </div>
                                  {step.optimized && (
                                    <div className="text-xs text-[#87A96B] font-medium">
                                      Parallélisé
                                    </div>
                                  )}
                                </div>
                              </div>
                              <p className="text-xs text-gray-600 mt-1 truncate">
                                <ExplanationWithTimes
                                  text={step.step}
                                  ingredients={relatedRecipe?.ingredients}
                                />
                              </p>
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setGeneratedPlan(null)}
                      className="flex-1 px-4 py-2 border border-gray-300 text-[#2C3E50] rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Modifier la sélection
                    </button>
                    <button
                      onClick={handleStartBatchCooking}
                      className="flex-1 px-4 py-2 bg-[#87A96B] text-white rounded-lg hover:bg-[#87A96B]/90 transition-colors"
                    >
                      Commencer le batch cooking
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Composant Timer compact pour le panel centralisé
  const CompactTimerCard = ({ timer }) => {
    const recipe = activeRecipes.find((r) => r.id === timer.recipeId);
    const progress = ((timer.duration - timer.timeLeft) / timer.duration) * 100;
    const highlight = timer.id === highlightTimerId;

    return (
      <div
        id={`timer-${timer.id}`}
        className={`p-2 rounded border transition-all ${
          timer.isFinished
            ? "border-red-400 bg-red-50 animate-pulse"
            : timer.isRunning
            ? "border-[#E67E22] bg-[#E67E22]/10"
            : "border-gray-200 bg-white"
        } ${highlight ? 'ring-2 ring-[#87A96B] animate-pulse' : ''}`}
      >
        <div className="flex items-center justify-between mb-1">
          <span className="font-medium text-xs truncate text-[#2C3E50]">{timer.label}</span>
          <button
            onClick={() => deleteTimer(timer.id)}
            className="text-gray-400 hover:text-red-500 transition-colors p-1"
          >
            <X className="w-3 h-3" />
          </button>
        </div>

        <div className="text-center mb-1">
          <div
            className={`text-sm font-bold ${
              timer.isFinished ? "text-red-600" : "text-[#2C3E50]"
            }`}
          >
            {formatTime(timer.timeLeft)}
          </div>
          {recipe && (
            <div className="text-xs text-gray-500 truncate">{recipe.title}</div>
          )}
        </div>

        <div className="w-full bg-gray-200 rounded-full h-1 mb-1">
          <div
            className={`h-1 rounded-full transition-all duration-1000 ${
              timer.isFinished ? "bg-red-400" : "bg-[#E67E22]"
            }`}
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        <div className="flex justify-center space-x-1">
          <button
            onClick={() => toggleTimer(timer.id)}
            className={`p-1 rounded text-xs ${
              timer.isRunning
                ? "bg-red-400 hover:bg-red-500 text-white"
                : "bg-[#87A96B] hover:bg-[#87A96B]/90 text-white"
            } transition-colors`}
          >
            {timer.isRunning ? (
              <Pause className="w-2 h-2" />
            ) : (
              <Play className="w-2 h-2" />
            )}
          </button>
          <button
            onClick={() => resetTimer(timer.id)}
            className="p-1 bg-[#2C3E50] hover:bg-[#2C3E50]/90 text-white rounded transition-colors text-xs"
          >
            <RotateCcw className="w-2 h-2" />
          </button>
        </div>

        {timer.isFinished && (
          <div className="mt-1 flex items-center justify-center text-red-600 text-xs font-medium">
            <AlertCircle className="w-2 h-2 mr-1" />
            Fini !
          </div>
        )}
      </div>
    );
  };

  // Panel flottant des timers centralisé
  const FloatingTimersPanel = () => {
    const activeTimers = timers.length;
    const runningTimers = timers.filter((timer) => timer.isRunning).length;
    const finishedTimers = timers.filter((timer) => timer.isFinished).length;

    if (activeTimers === 0) return null;

    return (
      <div id="floating-timers-panel" className="fixed right-4 bottom-20 sm:bottom-4 z-[60]">
        {/* Bouton toggle pour le panel */}
        <div className="relative">
          <button
            onClick={() => setShowAllTimers(!showAllTimers)}
            id="floating-timers-panel-toggle"
            className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white transition-all ${
              finishedTimers > 0
                ? "bg-red-500 hover:bg-red-600 animate-pulse"
                : runningTimers > 0
                ? "bg-[#E67E22] hover:bg-[#E67E22]/90"
                : "bg-[#2C3E50] hover:bg-[#2C3E50]/90"
            }`}
          >
            <Timer className="w-6 h-6" />
          </button>

          {/* Badge avec nombre de timers */}
          <div className="absolute -top-1 -right-1 bg-[#E67E22] text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
            {activeTimers}
          </div>

          {/* Indicateur timers finis */}
          {finishedTimers > 0 && (
            <div className="absolute -top-1 -left-1 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-bounce">
              !
            </div>
          )}
        </div>

        {/* Panel des timers */}
        {showAllTimers && (
          <div className="absolute bottom-16 right-0 w-80 max-h-96 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
            {/* Header du panel */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-[#2C3E50] flex items-center">
                  <Timer className="w-4 h-4 mr-2" />
                  Timers actifs ({activeTimers})
                </h3>
                <button
                  onClick={() => setShowAllTimers(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Statistiques rapides */}
              <div className="mt-2 flex items-center space-x-4 text-xs text-gray-600">
                <span className="flex items-center">
                  <div className="w-2 h-2 bg-[#E67E22] rounded-full mr-1"></div>
                  {runningTimers} en cours
                </span>
                <span className="flex items-center">
                  <div className="w-2 h-2 bg-gray-400 rounded-full mr-1"></div>
                  {activeTimers - runningTimers - finishedTimers} en pause
                </span>
                {finishedTimers > 0 && (
                  <span className="flex items-center">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-1"></div>
                    {finishedTimers} terminés
                  </span>
                )}
              </div>
            </div>

            {/* Liste des timers */}
            <div className="p-4 space-y-3 max-h-64 overflow-y-auto">
              {timers
                .sort((a, b) => {
                  // Prioriser les timers finis, puis en cours, puis en pause
                  if (a.isFinished && !b.isFinished) return -1;
                  if (!a.isFinished && b.isFinished) return 1;
                  if (a.isRunning && !b.isRunning) return -1;
                  if (!a.isRunning && b.isRunning) return 1;
                  return a.timeLeft - b.timeLeft;
                })
                .map((timer) => (
                  <CompactTimerCard key={timer.id} timer={timer} />
                ))}
            </div>

            {/* Actions globales */}
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    timers.forEach((timer) => {
                      if (!timer.isRunning && !timer.isFinished) {
                        toggleTimer(timer.id);
                      }
                    });
                  }}
                  disabled={timers.every(
                    (timer) => timer.isRunning || timer.isFinished
                  )}
                  className={`flex-1 px-3 py-2 text-xs rounded transition-colors ${
                    timers.every((timer) => timer.isRunning || timer.isFinished)
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-[#87A96B] text-white hover:bg-[#87A96B]/90"
                  }`}
                >
                  <Play className="w-3 h-3 inline mr-1" />
                  Tout démarrer
                </button>

                <button
                  onClick={() => {
                    // Stopper tous les sons d'alarme en cours
                    const map = alarmIntervalsRef.current || {};
                    Object.values(map).forEach((val) => {
                      if (typeof val === 'number') clearInterval(val);
                      else if (val && typeof val.pause === 'function') { try { val.pause(); val.currentTime = 0; val.loop = false; } catch {} }
                    });
                    alarmIntervalsRef.current = {};
                    // Mettre en pause tous les timers en cours
                    timers.forEach((timer) => {
                      if (timer.isRunning) {
                        toggleTimer(timer.id);
                      }
                    });
                  }}
                  disabled={!timers.some((timer) => timer.isRunning)}
                  className={`flex-1 px-3 py-2 text-xs rounded transition-colors ${
                    !timers.some((timer) => timer.isRunning)
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-red-500 text-white hover:bg-red-600"
                  }`}
                >
                  <Pause className="w-3 h-3 inline mr-1" />
                  Tout arrêter
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Sélecteur de recettes
  const RecipeSelector = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-lg max-w-md w-full max-h-96 overflow-y-auto">
        <div className="px-2 py-1 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-[#2C3E50]">Choisir une recette</h3>
            <button
              onClick={() => setShowRecipeSelector(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-3 py-2 space-y-2">
          {activeRecipes.map((recipe, index) => {
            const progress =
              ((recipe.currentStep + 1) / recipe.instructions.length) * 100;
            const isCompleted =
              recipe.currentStep === recipe.instructions.length - 1 &&
              recipe.completedSteps.includes(recipe.currentStep);

            return (
              <button
                key={recipe.id}
                onClick={() => goToRecipe(index)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  index === currentRecipeIndex
                    ? "border-[#87A96B] bg-[#87A96B]/10"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm truncate max-w-[70%] text-[#2C3E50]">
                    {recipe.title}
                  </span>
                  {isCompleted && (
                    <CheckCircle className="w-4 h-4 text-[#87A96B] flex-shrink-0" />
                  )}
                </div>

              <div className="flex items-center justify-between text-xs text-white bg-[#2C3E50]/80 px-3 py-1 rounded-md mb-2">
                <span>
                  Étape {recipe.currentStep + 1}/{recipe.instructions.length}
                </span>
                <span>{recipe.currentServings} portions</span>
              </div>

                <div className="w-full bg-gray-200 rounded-full h-1">
                  <div
                    className={`h-1 rounded-full ${
                      isCompleted ? "bg-[#87A96B]" : "bg-[#E67E22]"
                    }`}
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
  
  const toggleInitialRecipe = (id) => {
    setInitialSelection((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  
  // Si pas de recettes actives
  if (activeRecipes.length === 0) {
    return (
      <>
        {seo}
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-2">
          <div className="w-full max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-2 flex flex-col items-center w-full">
            <div className="mb-8 flex flex-col items-center justify-center">
              <img
                src="/img/start-cooking.png"
                alt="Chef"
                className="w-40 h-40 rounded-full shadow-xl mb-6 object-cover mx-auto"
              />
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#2C3E50] mb-2 text-center leading-tight">
                Commencer une{" "}
                <span className="text-[#87A96B]">Session Cuisine</span>{" "}
                maintenant
              </h1>
              <p className="text-gray-500 text-lg text-center">
                Sélectionnez vos recettes à cuisiner aujourd'hui
              </p>
            </div>
            <div className="w-full mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {readyToCookRecipes.map((recipe) => {
                  const selected = initialSelection.includes(recipe.id);
                  return (
                    <button
                      key={recipe.id}
                      onClick={() => toggleInitialRecipe(recipe.id)}
                      className={`
              relative flex flex-col items-center text-center bg-white rounded-3xl shadow-lg p-4 sm:p-6 transition-all border
              ${
                selected
                  ? "border-[#87A96B] ring-2 ring-[#87A96B]/20"
                  : "border-transparent hover:border-[#87A96B]/50 hover:ring-2 hover:ring-[#87A96B]/10"
              }
              `}
                    >
                      {/* Badge sélectionné */}
                      {selected && (
                        <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[#87A96B] flex items-center justify-center text-white shadow-md">
                          <CheckCircle className="w-4 h-4" />
                        </span>
                      )}

                      {/* Bannière image de la recette */}
                      <div className="w-full aspect-square rounded-2xl overflow-hidden shadow mb-4">
                        <img
                          src={
                            recipe.imgUrlArray.find(x => x.includes('/recipe')) ??
                            recipe.imgUrlArray.find(x => x.includes('/suggestion')) ??
                            "/img/placeholder-food.jpg"
                          }
                          alt={recipe.title}
                          className="object-cover w-full h-full"
                        />
                      </div>

                      {/* Titre + infos */}
                      <div className="space-y-1">
                        <h3 className="font-semibold text-[#2C3E50] text-sm sm:text-base">
                          {recipe.title}
                        </h3>
                        <div className="flex items-center justify-center text-gray-500 text-xs sm:text-sm gap-2">
                          <Clock className="w-4 h-4" />
                          {recipe.prepTime} min
                          <ChefHat className="w-4 h-4" />
                          {recipe.instructions.length} étapes
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            {initialSelection.length > 0 && (
              <div className="sticky bottom-4 left-0 w-full z-50 bg-white/90 backdrop-blur-sm px-4 sm:px-8">
                <button
                  disabled={initialSelection.length === 0}
                  onClick={() => {
                    initialSelection.forEach((id) => {
                      const recipe = readyToCookRecipes.find(
                        (r) => r.id === id
                      );
                      addRecipe(recipe);
                    });
                    setInitialSelection([]);
                  }}
      className={
        `w-full py-5 rounded-full font-bold text-xl sm:text-2xl shadow-xl transition-colors
        ${initialSelection.length > 0
          ? 'bg-[#87A96B] text-white hover:bg-[#87A96B]/90'
          : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`
      }
    >
      Commencer la session ({initialSelection.length})
                </button>
              </div>
            )}
            {initialSelection.length === 0 && (
              <button
                disabled
                className="w-full py-5 rounded-l-full rounded-r-full font-bold text-2xl shadow-xl bg-gray-200 text-gray-400 cursor-not-allowed"
              >
                Commencer la session (0)
              </button>
            )}
          </div>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      {seo}
      <div className="min-h-screen bg-gray-50">
      {/* Header avec navigation et progression globale */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        {/* Indicateur mode batch cooking */}
        {batchMode && batchPlan && (
          <div className="bg-[#E67E22]/10 px-4 py-3 border-t border-[#E67E22]/30">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-[#E67E22] rounded-full animate-pulse"></div>
                  <span className="font-semibold text-[#2C3E50]">
                    Mode Batch Cooking actif
                  </span>
                  <span className="text-sm text-[#2C3E50]">
                    {batchPlan.recipes.length} recettes • Économie estimée:{" "}
                    {formatDuration(batchPlan.estimatedSavings)}
                  </span>
                </div>
                <button
                  onClick={() => setBatchMode(false)}
                  className="text-[#E67E22] hover:text-[#E67E22]/80 text-sm"
                >
                  Désactiver
                </button>
              </div>

              {/* Progression du batch */}
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs text-[#2C3E50] mb-1">
                  <span>Progression de la session batch</span>
                  <span>
                    {(() => {
                      const totalBatchSteps = batchPlan.timeline.length;
                      const completedBatchSteps = activeRecipes.reduce(
                        (sum, recipe) => sum + recipe.completedSteps.length,
                        0
                      );
                      return `${Math.round(
                        (completedBatchSteps / totalBatchSteps) * 100
                      )}%`;
                    })()}
                  </span>
                </div>
                <div className="w-full bg-[#E67E22]/20 rounded-full h-2">
                  <div
                    className="bg-[#E67E22] h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${(() => {
                        const totalBatchSteps = batchPlan.timeline.length;
                        const completedBatchSteps = activeRecipes.reduce(
                          (sum, recipe) => sum + recipe.completedSteps.length,
                          0
                        );
                        return (completedBatchSteps / totalBatchSteps) * 100;
                      })()}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Progression globale de la session */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-[#2C3E50] text-sm">
                Progression de la session
              </h3>
              <span className="text-xs text-gray-600">
                {(() => {
                  const totalSteps = activeRecipes.reduce(
                    (sum, recipe) => sum + recipe.instructions.length,
                    0
                  );
                  const completedSteps = activeRecipes.reduce(
                    (sum, recipe) =>
                      sum +
                      recipe.completedSteps.length +
                      (recipe.currentStep > 0 ? 1 : 0),
                    0
                  );
                  const globalProgress =
                    totalSteps > 0
                      ? Math.round((completedSteps / totalSteps) * 100)
                      : 0;
                  return `${globalProgress}% (${completedSteps}/${totalSteps} étapes)`;
                })()}
              </span>
            </div>

            {/* Barre de progression globale */}
            <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-3 shadow-inner mb-4">
              <div
                className="h-3 rounded-full bg-gradient-to-r from-[#87A96B] to-[#87A96B]/80 transition-all duration-700 ease-out"
                style={{
                  width: `${(() => {
                    const totalSteps = activeRecipes.reduce(
                      (sum, recipe) => sum + recipe.instructions.length,
                      0
                    );
                    const completedSteps = activeRecipes.reduce(
                      (sum, recipe) =>
                        sum +
                        recipe.completedSteps.length +
                        (recipe.currentStep > 0 ? 1 : 0),
                      0
                    );
                    return totalSteps > 0
                      ? (completedSteps / totalSteps) * 100
                      : 0;
                  })()}%`,
                }}
              ></div>
            </div>

            {/* Carousel de progression par recette */}
            <RecipeCarousel
              activeRecipes={activeRecipes}
              currentRecipeIndex={currentRecipeIndex}
              setCurrentRecipeIndex={setCurrentRecipeIndex}
              goToRecipe={goToRecipe}
            />
          </div>
        </div>
      </div>

      {activeRecipes.map((recipe, index) => (
        <RecipeRender
          key={recipe.id}
          activeRecipesLength={activeRecipes.length}
          removeRecipe={removeRecipe}
          recipe={recipe}
          toDisplay={currentRecipe && recipe.id === currentRecipe.id}
          timers={timers}
          updateServings={updateServings}
          createTimer={createTimer}
          startTimerFromText={startTimerFromText}
          previousStep={previousStep}
          nextStep={nextStep}
          setShowRecipeSelector={setShowRecipeSelector}
          CompactTimerCard={CompactTimerCard}
          readyToCookRecipes={readyToCookRecipes}
          activeRecipes={activeRecipes}
          addRecipe={addRecipe}
          currentRecipeIndex={currentRecipeIndex}
          goToRecipe={goToRecipe}
        />
      ))}
      {/* Modal de sélection des recettes */}
      {showRecipeSelector && <RecipeSelector />}

      {/* Modal du planificateur batch cooking */}
      {showBatchPlanner && <BatchPlanner />}

      {/* Panel flottant des timers centralisé */}
      <FloatingTimersPanel />
    </div>
    </>
  );
};

export default CookingAssistantPage;
